import React, { useEffect, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Sparkles, Send, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import ReviewForm, { StageAnswers } from './ReviewForm';
import ReportCard from './ReportCard';

const today = () => new Date().toISOString().slice(0, 10);

const STATUS_BADGE = {
  self: ['Awaiting self-review', 'bg-amber-100 text-amber-800 border-amber-300'],
  manager: ['Awaiting manager', 'bg-blue-100 text-blue-800 border-blue-300'],
  director: ['Awaiting director', 'bg-purple-100 text-purple-800 border-purple-300'],
  complete: ['Complete', 'bg-green-100 text-green-800 border-green-300'],
};

export default function PerfReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ employee_id: '', supervisor_id: '', period: '', due_date: '' });
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [summaryText, setSummaryText] = useState('');
  const [details, setDetails] = useState({ due_date: '', year_label: '2027', title: '', details: '' });
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [r, e] = await Promise.all([api.get('/perf/reviews'), api.get('/employees')]);
      setReviews(r.data);
      setEmployees((e.data || []).filter(u => u.is_active !== false));
    } catch { toast.error('Could not load reviews'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    setOpenId(id);
    const { data } = await api.get(`/perf/reviews/${id}`);
    setDetail(data);
    setSummaryText(data.summary?.text || '');
    const nr = data.next_role || {};
    setDetails({ due_date: data.due_date || '', year_label: nr.year_label || '2027', title: nr.title || '', details: nr.details || '' });
  };

  const saveDetails = async () => {
    setBusy('details');
    try {
      await api.put(`/perf/reviews/${openId}/details`, {
        due_date: details.due_date,
        next_role: { year_label: details.year_label, title: details.title, details: details.details },
      });
      toast.success('Details saved');
      await openDetail(openId); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy('');
  };

  const create = async () => {
    if (!form.employee_id || !form.supervisor_id) { toast.error('Pick an employee and a supervisor'); return; }
    setBusy('create');
    try {
      await api.post('/perf/reviews', form);
      toast.success('Review opened — the employee has been notified to complete their self-review');
      setShowCreate(false);
      setForm({ employee_id: '', supervisor_id: '', period: '', due_date: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create review'); }
    setBusy('');
  };

  const submitDirector = async (answers) => {
    setBusy('director');
    try {
      await api.post(`/perf/reviews/${openId}/submit`, { stage: 'director', answers });
      toast.success('Director review submitted — review is complete');
      await openDetail(openId); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
    setBusy('');
  };

  const genSummary = async () => {
    setBusy('gen');
    try {
      const { data } = await api.post(`/perf/reviews/${openId}/summary/generate`);
      setSummaryText(data.text);
      toast.success('AI draft ready — edit as needed, then send');
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    setBusy('');
  };

  const saveSummary = async () => {
    setBusy('save');
    try { await api.put(`/perf/reviews/${openId}/summary`, { text: summaryText }); toast.success('Draft saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy('');
  };

  const sendSummary = async () => {
    if (!window.confirm(`Send this summary to ${detail.employee_name}? They'll see it under My Reviews.`)) return;
    setBusy('send');
    try {
      await api.put(`/perf/reviews/${openId}/summary`, { text: summaryText });
      await api.post(`/perf/reviews/${openId}/summary/send`);
      toast.success('Summary sent to the employee');
      await openDetail(openId); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setBusy('');
  };

  const del = async (id) => {
    if (!window.confirm('Delete this review and all its answers?')) return;
    await api.delete(`/perf/reviews/${id}`);
    toast.success('Review deleted');
    if (openId === id) { setOpenId(null); setDetail(null); }
    load();
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#6B3FA0]" /></div>;

  return (
    <div className="space-y-3" data-testid="perf-reviews-tab">
      <Button onClick={() => setShowCreate(true)} className="bg-[#6B3FA0]" data-testid="new-review-btn">
        <Plus className="h-4 w-4 mr-1" /> New review
      </Button>

      <div className="rounded-xl border border-[#DED8EB] bg-white divide-y divide-[#F0ECF7]">
        {reviews.map(r => {
          const [label, cls] = STATUS_BADGE[r.status] || [r.status, 'bg-gray-100 text-gray-700'];
          return (
            <button key={r.id} onClick={() => openDetail(r.id)} data-testid={`review-row-${r.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#FBFAFD]">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#2D1B4E]">{r.employee_name}</p>
                <p className="text-[11px] text-[#8E85A0]">{r.period} · supervisor: {r.supervisor_name}</p>
              </div>
              {r.summary?.sent_at && <span className="text-[10px] font-bold text-[#2D6A4F]">SUMMARY SENT</span>}
              {r.due_date && r.status !== 'complete' && (
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${r.due_date < today() ? 'bg-red-100 text-red-800 border-red-300' : 'bg-[#FFF3E0] text-[#B45309] border-[#FFD9A8]'}`}>
                  {r.due_date < today() ? `Overdue ${r.due_date}` : `Due ${r.due_date}`}
                </span>
              )}
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${cls}`}>{label}</span>
            </button>
          );
        })}
        {reviews.length === 0 && <p className="px-3 py-8 text-center text-sm text-[#8E85A0]">No reviews yet — open one with "New review".</p>}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-review-dialog">
          <DialogHeader><DialogTitle>Start a performance review</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-[#5A4D6E]">Employee (self-review goes to)</label>
              <select className="w-full mt-1 rounded-md border border-[#DED8EB] p-2 text-sm" value={form.employee_id}
                      onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))} data-testid="create-employee-select">
                <option value="">Select employee…</option>
                {employees.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-[#5A4D6E]">Supervisor (manager review goes to)</label>
              <select className="w-full mt-1 rounded-md border border-[#DED8EB] p-2 text-sm" value={form.supervisor_id}
                      onChange={e => setForm(f => ({ ...f, supervisor_id: e.target.value }))} data-testid="create-supervisor-select">
                <option value="">Select supervisor…</option>
                {employees.filter(u => ['supervisor', 'administrator', 'admin'].includes(u.role)).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-[#5A4D6E]">Review period label</label>
              <Input value={form.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))}
                     placeholder="e.g. 2026 Annual Review" data-testid="create-period-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#5A4D6E]">Due date (optional — drives login alerts & reminders)</label>
              <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                     data-testid="create-due-input" />
            </div>
            <Button onClick={create} disabled={busy === 'create'} className="bg-[#6B3FA0] w-full" data-testid="create-review-submit">
              {busy === 'create' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Open review & notify employee'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!openId} onOpenChange={(v) => { if (!v) { setOpenId(null); setDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="review-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between pr-6">
                  <span>{detail.employee_name} — {detail.period}</span>
                  <Button variant="ghost" size="icon" onClick={() => del(detail.id)} className="text-[#D9534F]" data-testid="delete-review-btn"><Trash2 className="h-4 w-4" /></Button>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {!detail.summary?.sent_at && (
                  <div className="rounded-xl border border-[#DED8EB] bg-[#FBFAFD] p-3 space-y-2" data-testid="details-editor">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#9B8FAE]">Due date & report card extras</p>
                    <div className="grid sm:grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] font-bold text-[#5A4D6E]">Due date</label>
                        <Input type="date" value={details.due_date} onChange={e => setDetails(d => ({ ...d, due_date: e.target.value }))} data-testid="detail-due-input" />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-[#5A4D6E]">Role year</label>
                        <Input value={details.year_label} onChange={e => setDetails(d => ({ ...d, year_label: e.target.value }))} placeholder="2027" data-testid="detail-year-input" />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-[#5A4D6E]">{details.year_label || '2027'} role title</label>
                        <Input value={details.title} onChange={e => setDetails(d => ({ ...d, title: e.target.value }))} placeholder="e.g. Lead DSP — Explore Program" data-testid="detail-roletitle-input" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-[#5A4D6E]">{details.year_label || '2027'} role details (shown on the branded report card)</label>
                      <Textarea rows={3} value={details.details} onChange={e => setDetails(d => ({ ...d, details: e.target.value }))}
                                placeholder="Responsibilities, schedule, compensation changes, expectations for next year…" data-testid="detail-roledetails-input" />
                    </div>
                    <Button size="sm" variant="outline" onClick={saveDetails} disabled={busy === 'details'} data-testid="save-details-btn">
                      {busy === 'details' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save details
                    </Button>
                  </div>
                )}

                <StageAnswers stage="self" label="Self-review" review={detail} />
                <StageAnswers stage="manager" label="Manager review" review={detail} />
                <StageAnswers stage="director" label="Director review" review={detail} />

                {detail.status === 'self' && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">Waiting on {detail.employee_name}'s self-review.</p>}
                {detail.status === 'manager' && <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-2">Waiting on {detail.supervisor_name}'s manager review.</p>}

                {detail.status === 'director' && (
                  <div>
                    <p className="text-sm font-black text-[#6B3FA0] uppercase tracking-wide mb-2">Director review — your turn</p>
                    <ReviewForm questions={detail.questions} onSubmit={submitDirector} submitting={busy === 'director'} submitLabel="Submit director review" />
                  </div>
                )}

                {detail.status === 'complete' && detail.summary?.sent_at && (
                  <ReportCard data={{
                    id: detail.id,
                    employee_name: detail.employee_name,
                    supervisor_name: detail.supervisor_name,
                    period: detail.period,
                    text: detail.summary.text,
                    sent_at: detail.summary.sent_at,
                    sent_by: detail.summary.sent_by,
                    completed_at: detail.stages?.director?.submitted_at || '',
                    next_role: detail.next_role || {},
                    final_ratings: Object.entries(detail.stages?.director?.answers || {})
                      .map(([qid, score]) => {
                        const q = (detail.questions || []).find(x => x.id === qid);
                        return q && q.type === 'rating' ? { category: q.category, text: q.text, score } : null;
                      }).filter(Boolean).sort((a, b) => a.category.localeCompare(b.category)),
                  }} />
                )}

                {detail.status === 'complete' && !detail.summary?.sent_at && (
                  <div className="rounded-xl border border-[#C9B8E8] bg-[#FBF8FF] p-3 space-y-2" data-testid="summary-section">
                    <p className="text-sm font-black text-[#6B3FA0] uppercase tracking-wide">Review summary</p>
                    <Button onClick={genSummary} disabled={busy === 'gen'} variant="outline" className="border-[#6B3FA0] text-[#6B3FA0]" data-testid="generate-summary-btn">
                      {busy === 'gen' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                      {summaryText ? 'Regenerate AI draft' : 'Generate AI draft'}
                    </Button>
                    <Textarea rows={12} value={summaryText} onChange={e => setSummaryText(e.target.value)}
                              placeholder="Generate an AI draft or write the summary here…"
                              data-testid="summary-textarea" />
                    <div className="flex gap-2">
                      <Button onClick={saveSummary} disabled={busy === 'save' || !summaryText.trim()} variant="outline" data-testid="save-summary-btn">
                        <Save className="h-4 w-4 mr-1" /> Save draft
                      </Button>
                      <Button onClick={sendSummary} disabled={busy === 'send' || !summaryText.trim()} className="bg-[#2D6A4F]" data-testid="send-summary-btn">
                        <Send className="h-4 w-4 mr-1" /> Send to {detail.employee_name}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
